import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  MenuItem,
} from '@mui/material';
import { getDeliveryConfig, updateDeliveryConfig, DeliveryConfig } from '../services/deliveryConfigService';

const clampNumber = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

type Props = {
  groupId?: string | null;
};

const DriverSettings: React.FC<Props> = ({ groupId }) => {
  const [cfg, setCfg] = useState<DeliveryConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const groupingMode = (cfg?.groupingMode ?? 'radius') as DeliveryConfig['groupingMode'];
  const isRouteMode = groupingMode === 'route';

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const c = await getDeliveryConfig(groupId);
        if (mounted) {
          setCfg(c);
        }
      } catch (e: any) {
        if (mounted) setError(e?.message || 'Erreur de chargement de la configuration');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [groupId]);

  const onNum = (key: keyof DeliveryConfig, clamp?: { min: number; max: number }) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const num = Number(raw);
    if (isNaN(num)) return;
    const v = clamp ? clampNumber(num, clamp.min, clamp.max) : num;
    setCfg((prev) => (prev ? { ...prev, [key]: v } as DeliveryConfig : prev));
    setSaved(false);
  };

  const onSwitch = (key: keyof DeliveryConfig) => (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setCfg((prev) => (prev ? { ...prev, [key]: checked } as DeliveryConfig : prev));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!cfg) return;
    try {
      setSaving(true);
      setError(null);

      // Respect CF clamps/ranges:
      // - timeoutMinutes: 0.5..30
      // - maxAttempts: 1..10
      // - retryIntervalMinutes: 1..60
      // - networkLatencyBufferSeconds: 5..30
      // - ttsIntervalSeconds: 5..20
      // - ttsFirstDelaySeconds: 0..5
      // - vibrateIntervalSeconds: 5..30
      // - maxDeliveryRadiusKm: 1..100
      // - freeKeepAliveMinutes: 1..30
      // - locationRecencyMinutes: 10..120
      const clamped: Partial<DeliveryConfig> = {
        timeoutMinutes: clampNumber(cfg.timeoutMinutes, 0.5, 30),
        maxAttempts: clampNumber(cfg.maxAttempts, 1, 10),
        retryIntervalMinutes: clampNumber(cfg.retryIntervalMinutes, 1, 60),
        enableAutoRetry: !!cfg.enableAutoRetry,
        networkLatencyBufferSeconds: clampNumber(cfg.networkLatencyBufferSeconds ?? 20, 5, 30),
        enableOfferReminders: false,

        ttsEnabled: !!cfg.ttsEnabled,
        ttsIntervalSeconds: clampNumber(cfg.ttsIntervalSeconds ?? 8, 5, 20),
        ttsFirstDelaySeconds: clampNumber(cfg.ttsFirstDelaySeconds ?? 2, 0, 5),
        vibrateEnabled: !!cfg.vibrateEnabled,
        vibrateIntervalSeconds: clampNumber(cfg.vibrateIntervalSeconds ?? 10, 5, 30),

        maxDeliveryRadiusKm: clampNumber(cfg.maxDeliveryRadiusKm ?? 10, 1, 100),

        weightProximity: Number(cfg.weightProximity),
        weightRating: Number(cfg.weightRating),
        weightAcceptance: Number(cfg.weightAcceptance),
        bonusFavorite: Number(cfg.bonusFavorite),

        // New tracking-related fields
        freeKeepAliveMinutes: clampNumber((cfg.freeKeepAliveMinutes ?? 3), 1, 30),
        locationRecencyMinutes: clampNumber((cfg.locationRecencyMinutes ?? 30), 10, 120),

        // Order grouping fields
        enableOrderGrouping: !!cfg.enableOrderGrouping,
        groupingRadiusKm: clampNumber((cfg.groupingRadiusKm ?? 1.5), 0.5, 5),
        maxOrdersPerBatch: clampNumber((cfg.maxOrdersPerBatch ?? 2), 2, 5),
        groupingDecisionWindowSeconds: clampNumber((cfg.groupingDecisionWindowSeconds ?? 120), 30, 600),
        groupingMode: (cfg.groupingMode === 'route' ? 'route' : 'radius'),
        groupingDetourMaxKm: clampNumber((cfg.groupingDetourMaxKm ?? 1.2), 0.2, 5),
        groupingDetourMaxRatio: clampNumber((cfg.groupingDetourMaxRatio ?? 0.2), 0, 1),
        groupingCorridorWidthKm: clampNumber((cfg.groupingCorridorWidthKm ?? 0.8), 0.2, 2),
        groupingMinRouteDistanceKm: clampNumber((cfg.groupingMinRouteDistanceKm ?? 1.0), 0.5, 5),
        groupingShortTripRadiusKm: clampNumber((cfg.groupingShortTripRadiusKm ?? 1.5), 0.5, 5),
        groupingOnlyAcceptedByDriver: !!cfg.groupingOnlyAcceptedByDriver,
        groupingRequireNotReady: !!cfg.groupingRequireNotReady,
      };

      const updated = await updateDeliveryConfig(clamped, { groupId });
      setCfg(updated);
      setSaved(true);
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Paramètres livreurs
      </Typography>

      {loading ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} /> <Typography>Chargement…</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : !cfg ? null : (
        <Stack spacing={2}>
          {groupId && (
            <Alert severity="info">
              Ces paramètres s’appliquent uniquement au groupe actif. Change de groupe en haut
              de page pour modifier une autre flotte.
            </Alert>
          )}
          {saved && <Alert severity="success">Paramètres enregistrés.</Alert>}

          {/* 1) Livraison & Délais */}
          <Card variant="outlined">
            <CardHeader title="Livraison & Délais" />
            <CardContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                <Box>
                  <Tooltip title="Durée d’une offre envoyée au livreur avant expiration côté app. 1 min = 60s.">
                    <TextField
                      fullWidth
                      type="number"
                      label="timeoutMinutes (min)"
                      value={cfg.timeoutMinutes}
                      onChange={onNum('timeoutMinutes', { min: 0.5, max: 30 })}
                      inputProps={{ step: 0.5, min: 0.5, max: 30 }}
                      helperText="Fenêtre d'acceptation côté app livreur (0.5..30)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Nombre d'essais séquentiels avant NO_DRIVER_FOUND.">
                    <TextField
                      fullWidth
                      type="number"
                      label="maxAttempts"
                      value={cfg.maxAttempts}
                      onChange={onNum('maxAttempts', { min: 1, max: 10 })}
                      inputProps={{ step: 1, min: 1, max: 10 }}
                      helperText="1..10"
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Intervalle entre relances (si relance auto activée).">
                    <TextField
                      fullWidth
                      type="number"
                      label="retryIntervalMinutes (min)"
                      value={cfg.retryIntervalMinutes}
                      onChange={onNum('retryIntervalMinutes', { min: 1, max: 60 })}
                      inputProps={{ step: 1, min: 1, max: 60 }}
                      helperText="1..60"
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!cfg.enableAutoRetry}
                        onChange={onSwitch('enableAutoRetry')}
                      />
                    }
                    label="enableAutoRetry"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Active la relance automatique côté backend, sans action magasin.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 2) Rappels & Latence Réseau */}
          <Card variant="outlined">
            <CardHeader title="Rappels & Latence Réseau" />
            <CardContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                <Box>
                  <Tooltip title="Tampon serveur ajouté au timeout app pour absorber la latence réseau.">
                    <TextField
                      fullWidth
                      type="number"
                      label="networkLatencyBufferSeconds (sec)"
                      value={cfg.networkLatencyBufferSeconds ?? 20}
                      onChange={onNum('networkLatencyBufferSeconds', { min: 5, max: 30 })}
                      inputProps={{ step: 1, min: 5, max: 30 }}
                      helperText="Recommandé: ~20s (plage 5..30)."
                    />
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 3) Annonces (TTS & Vibration) */}
          <Card variant="outlined">
            <CardHeader title="Annonces (TTS & Vibration)" />
            <CardContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!cfg.ttsEnabled}
                        onChange={onSwitch('ttsEnabled')}
                      />
                    }
                    label="ttsEnabled"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Active la synthèse vocale côté app livreur pendant l’offre.
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title="Périodicité des annonces vocales (sec).">
                    <TextField
                      fullWidth
                      type="number"
                      label="ttsIntervalSeconds (sec)"
                      value={cfg.ttsIntervalSeconds ?? 8}
                      onChange={onNum('ttsIntervalSeconds', { min: 5, max: 20 })}
                      inputProps={{ step: 1, min: 5, max: 20 }}
                      disabled={!cfg.ttsEnabled}
                      helperText="5..20 sec (ex: 8)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Délai avant la première annonce vocale (sec).">
                    <TextField
                      fullWidth
                      type="number"
                      label="ttsFirstDelaySeconds (sec)"
                      value={cfg.ttsFirstDelaySeconds ?? 2}
                      onChange={onNum('ttsFirstDelaySeconds', { min: 0, max: 5 })}
                      inputProps={{ step: 1, min: 0, max: 5 }}
                      disabled={!cfg.ttsEnabled}
                      helperText="0..5 sec (ex: 2)."
                    />
                  </Tooltip>
                </Box>

                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!cfg.vibrateEnabled}
                        onChange={onSwitch('vibrateEnabled')}
                      />
                    }
                    label="vibrateEnabled"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Active la vibration périodique côté app livreur.
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title="Périodicité des vibrations (sec).">
                    <TextField
                      fullWidth
                      type="number"
                      label="vibrateIntervalSeconds (sec)"
                      value={cfg.vibrateIntervalSeconds ?? 10}
                      onChange={onNum('vibrateIntervalSeconds', { min: 5, max: 30 })}
                      inputProps={{ step: 1, min: 5, max: 30 }}
                      disabled={!cfg.vibrateEnabled}
                      helperText="5..30 sec (ex: 10)."
                    />
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 4) Localisation & Tracking */}
          <Card variant="outlined">
            <CardHeader title="Localisation & Tracking" />
            <CardContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                <Box>
                  <Tooltip title="Intervalle keep-alive côté app livreur quand il est en ligne et 'free'. Une position est poussée au moins toutes les N minutes, même immobile.">
                    <TextField
                      fullWidth
                      type="number"
                      label="freeKeepAliveMinutes (min)"
                      value={cfg.freeKeepAliveMinutes ?? 3}
                      onChange={onNum('freeKeepAliveMinutes', { min: 1, max: 30 })}
                      inputProps={{ step: 1, min: 1, max: 30 }}
                      helperText="1..30 min (défaut 3)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Fraîcheur max (minutes) de lastLocationUpdate acceptée par le backend pour considérer un livreur éligible.">
                    <TextField
                      fullWidth
                      type="number"
                      label="locationRecencyMinutes (min)"
                      value={cfg.locationRecencyMinutes ?? 30}
                      onChange={onNum('locationRecencyMinutes', { min: 10, max: 120 })}
                      inputProps={{ step: 5, min: 10, max: 120 }}
                      helperText="10 à 120 min. Valeur conseillée: 30 min."
                    />
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 5) Regroupement des Commandes */}
          <Card variant="outlined">
            <CardHeader title="Regroupement des Commandes" />
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Permet de joindre plusieurs commandes proches à un même livreur pour optimiser les livraisons.
                Le store manager verra une option "Joindre" quand une commande est détectée proche d'une livraison en cours.
                La période d'éligibilité est limitée entre PREPARING et la commande prête (pas de fenêtre minute).
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!cfg.enableOrderGrouping}
                        onChange={onSwitch('enableOrderGrouping')}
                      />
                    }
                    label="Activer le regroupement"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Activer la détection et le regroupement des commandes proches. Désactivé par défaut.
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title="Mode de détection. radius = cercle de proximité (client↔client). route = corridor + détour basé sur store→client.">
                    <TextField
                      fullWidth
                      select
                      label="groupingMode"
                      value={groupingMode ?? 'radius'}
                      onChange={(e) => {
                        const v = e.target.value === 'route' ? 'route' : 'radius';
                        setCfg((prev) => (prev ? { ...prev, groupingMode: v } as DeliveryConfig : prev));
                        setSaved(false);
                      }}
                      disabled={!cfg.enableOrderGrouping}
                      helperText="radius | route"
                    >
                      <MenuItem value="radius">radius</MenuItem>
                      <MenuItem value="route">route</MenuItem>
                    </TextField>
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Rayon de proximité utilisé pour rapprocher des commandes proches, ou pour les trajets très courts.">
                    <TextField
                      fullWidth
                      type="number"
                      label="groupingRadiusKm (km)"
                      value={cfg.groupingRadiusKm ?? 1.5}
                      onChange={onNum('groupingRadiusKm', { min: 0.5, max: 5 })}
                      inputProps={{ step: 0.5, min: 0.5, max: 5 }}
                      disabled={!cfg.enableOrderGrouping}
                      helperText="0.5..5 km (défaut 1.5)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Temps accordé au magasin pour décider du regroupement avant l’assignation auto.">
                    <TextField
                      fullWidth
                      type="number"
                      label="Fenêtre décision regroupement (sec)"
                      value={cfg.groupingDecisionWindowSeconds ?? 120}
                      onChange={onNum('groupingDecisionWindowSeconds', { min: 30, max: 600 })}
                      inputProps={{ step: 10, min: 30, max: 600 }}
                      disabled={!cfg.enableOrderGrouping}
                      helperText="30..600 s (défaut 120)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Nombre maximum de commandes pouvant être groupées dans un même batch pour un livreur.">
                    <TextField
                      fullWidth
                      type="number"
                      label="maxOrdersPerBatch"
                      value={cfg.maxOrdersPerBatch ?? 2}
                      onChange={onNum('maxOrdersPerBatch', { min: 2, max: 5 })}
                      inputProps={{ step: 1, min: 2, max: 5 }}
                      disabled={!cfg.enableOrderGrouping}
                      helperText="2..5 commandes (défaut 2)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Largeur du couloir autour de la route store→client pour accepter une commande sur l'itinéraire.">
                    <TextField
                      fullWidth
                      type="number"
                      label="groupingCorridorWidthKm (km)"
                      value={cfg.groupingCorridorWidthKm ?? 0.8}
                      onChange={onNum('groupingCorridorWidthKm', { min: 0.2, max: 2 })}
                      inputProps={{ step: 0.1, min: 0.2, max: 2 }}
                      disabled={!cfg.enableOrderGrouping || !isRouteMode}
                      helperText="0.2..2 km (défaut 0.8)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Détour maximal autorisé (km) pour ajouter une commande sur la route store→client.">
                    <TextField
                      fullWidth
                      type="number"
                      label="groupingDetourMaxKm (km)"
                      value={cfg.groupingDetourMaxKm ?? 1.2}
                      onChange={onNum('groupingDetourMaxKm', { min: 0.2, max: 5 })}
                      inputProps={{ step: 0.1, min: 0.2, max: 5 }}
                      disabled={!cfg.enableOrderGrouping || !isRouteMode}
                      helperText="0.2..5 km (défaut 1.2)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Détour maximal en pourcentage par rapport à la distance store→client (ex: 0.2 = +20%).">
                    <TextField
                      fullWidth
                      type="number"
                      label="groupingDetourMaxRatio"
                      value={cfg.groupingDetourMaxRatio ?? 0.2}
                      onChange={onNum('groupingDetourMaxRatio', { min: 0, max: 1 })}
                      inputProps={{ step: 0.05, min: 0, max: 1 }}
                      disabled={!cfg.enableOrderGrouping || !isRouteMode}
                      helperText="0..1 (défaut 0.2)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Distance minimale magasin→client pour utiliser le mode trajet. En dessous, le mode proximité reste utilisé.">
                    <TextField
                      fullWidth
                      type="number"
                      label="groupingMinRouteDistanceKm (km)"
                      value={cfg.groupingMinRouteDistanceKm ?? 1.0}
                      onChange={onNum('groupingMinRouteDistanceKm', { min: 0.5, max: 5 })}
                      inputProps={{ step: 0.1, min: 0.5, max: 5 }}
                      disabled={!cfg.enableOrderGrouping || !isRouteMode}
                      helperText="0.5..5 km (défaut 1.0)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Rayon de proximité utilisé quand le trajet magasin→client est trop court pour un regroupement par trajet.">
                    <TextField
                      fullWidth
                      type="number"
                      label="groupingShortTripRadiusKm (km)"
                      value={cfg.groupingShortTripRadiusKm ?? 1.5}
                      onChange={onNum('groupingShortTripRadiusKm', { min: 0.5, max: 5 })}
                      inputProps={{ step: 0.1, min: 0.5, max: 5 }}
                      disabled={!cfg.enableOrderGrouping}
                      helperText="0.5..5 km (défaut 1.5)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!cfg.groupingOnlyAcceptedByDriver}
                        onChange={onSwitch('groupingOnlyAcceptedByDriver')}
                      />
                    }
                    label="Uniquement commandes acceptées par un livreur"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Considère uniquement les commandes déjà acceptées par un livreur.
                  </Typography>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!cfg.groupingRequireNotReady}
                        onChange={onSwitch('groupingRequireNotReady')}
                      />
                    }
                    label="Exclure les commandes déjà prêtes"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Exclut les commandes déjà marquées prêtes.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* 6) Matching & Scoring */}
          <Card variant="outlined">
            <CardHeader title="Matching & Scoring" />
            <CardContent>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
                <Box>
                  <Tooltip title="Rayon maximal autour du magasin/client pour considérer un livreur.">
                    <TextField
                      fullWidth
                      type="number"
                      label="maxDeliveryRadiusKm (km)"
                      value={cfg.maxDeliveryRadiusKm ?? 10}
                      onChange={onNum('maxDeliveryRadiusKm', { min: 1, max: 100 })}
                      inputProps={{ step: 1, min: 1, max: 100 }}
                      helperText="1..100 km (ex: 10)."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Poids du critère distance (0..1).">
                    <TextField
                      fullWidth
                      type="number"
                      label="weightProximity"
                      value={cfg.weightProximity}
                      onChange={onNum('weightProximity')}
                      inputProps={{ step: 0.1 }}
                      helperText="Utilisé dans le scoring (0..1). Recommandé: ajuster par pas de 0.1."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Poids du critère note moyenne (0..1).">
                    <TextField
                      fullWidth
                      type="number"
                      label="weightRating"
                      value={cfg.weightRating}
                      onChange={onNum('weightRating')}
                      inputProps={{ step: 0.1 }}
                      helperText="Utilisé dans le scoring (0..1). Recommandé: ajuster par pas de 0.1."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Poids du critère taux d'acceptation (0..1).">
                    <TextField
                      fullWidth
                      type="number"
                      label="weightAcceptance"
                      value={cfg.weightAcceptance}
                      onChange={onNum('weightAcceptance')}
                      inputProps={{ step: 0.1 }}
                      helperText="Utilisé dans le scoring (0..1). Recommandé: ajuster par pas de 0.1."
                    />
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Bonus si le livreur est favori du magasin/client.">
                    <TextField
                      fullWidth
                      type="number"
                      label="bonusFavorite"
                      value={cfg.bonusFavorite}
                      onChange={onNum('bonusFavorite')}
                      inputProps={{ step: 10 }}
                      helperText="Utilisé dans le scoring. Exemple: 100 pour favoriser fortement les favoris."
                    />
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
};

export default DriverSettings;
